import {ShapeCommand} from '../commands.template';
import {EventEmitter} from '@angular/core';
import {KEYS_TO_SANITIZE} from '../../shared/constants/keys-to-sanitize.constants';
import {AttachmentType} from '@q9elements/ui-kit/common';

export class ActivityAddAttachmentCommand extends ShapeCommand {
  services: any;
  firstAction: boolean;
  mxKey: number;
  eventCb: EventEmitter<boolean>;
  constructor(data) {
    super(data);
    this.firstAction = true;
  }

  public injectCallBackEmitter(emitter: EventEmitter<boolean>): this {
    this.eventCb = emitter;

    return this;
  }

  public execute() {
    let action;

    this.mxKey = this.object.mxKey || this.getMxKey(this.object.itemMxKey, this.object.type);
    const param = {
      mxKey: this.mxKey,
      type: this.object.type,
      itemMxKey: this.object.itemMxKey,
      title: this.object.title,
      value: this.object.value || '',
      groups: this.object.groups,
      dataStructureDefinition: this.object.dataStructureDefinition,
      refModel: this.object.refModel,
      refModelNode: this.object.refModelNode,
      description: this.object.description,
      refModelName: this.object.refModelName,
      nodePath: this.object.nodePath,
      externalUrl: this.object.externalUrl,
      linkType: this.object.linkType,
      lightningExternalUrl: this.object.lightningExternalUrl,
      savedOnS3: this.object.savedOnS3,
      content: this.object.content,
      noteProItems: this.object.noteProItems,

      // ORG NODE
      sfNode: this.object.sfNode,
      nodeDescription: this.object.nodeDescription,
      nodeSummary: this.object.nodeSummary,
      nodeApiName: this.object.nodeApiName,
      nodeObjectParent: this.object.nodeObjectParent,
      objectType: this.object.objectType,
      displayInUserHelp: this.object.displayInUserHelp,
      displayInUserHelpClassic: this.object.displayInUserHelpClassic,
      pushedAsSfHelpDate: this.object.pushedAsSfHelpDate,

      // REQUIREMENT
      requirement: this.object.requirement,
      customFields: this.object.customFields,
      reqId: this.object.reqId,
      reqReason: this.object.reqReason,
      reqDetails: this.object.reqDetails,

      // STORY
      story: this.object.story,
      storyId: this.object.storyId,
      status: this.object.status,
      issueUrl: this.object.issueUrl,

      tags: this.object.tags,
      urlEntry: this.object.urlEntry,
      url: this.object.url
    };

    this.services.sanitizeXssService.sanitizeXss(param, KEYS_TO_SANITIZE.ATTACHMENT);

    if (this.object.refModel) {
      action = {createRefModelAttachment: param};
    } else if (this.object.requirement) {
      action = {createRequirementAttachment: param};

      return this.addStoryOrRequirement(action, param, 'requirement');
    } else if (this.object.story) {
      action = {createStoryAttachment: param};

      return this.addStoryOrRequirement(action, param, 'story');
    } else if (this.object.sfNode) {
      action = {createOrgModelAttachment: param};
    } else {
      action = {createAttachment: param};
    }

    if (this.object.type === 'datatable') {
      return this.services.diagramUpdateSvc.update(action, true, true).then(() => {
        this.services.jsonHandlerSvc.update(action);
        this.redrawActivity(this.object.itemMxKey);

        return param;
      });
    }

    const isUrlAttachment =
      this.object.type === AttachmentType.URL || this.object.type === AttachmentType.URL_ENTRY;

    if (isUrlAttachment) {
      return this.services.diagramUpdateSvc
        .update(action, true, isUrlAttachment, false, this.eventCb)
        .then(result => {
          this.services.jsonHandlerSvc.update(action);

          this.redrawActivity(this.object.itemMxKey);

          return result;
        });
    }

    return this.services.diagramUpdateSvc
      .update(action, true, false, false, this.eventCb)
      .then(result => {
        this.redrawActivity(this.object.itemMxKey);

        return result;
      });
  }

  public reverse() {
    let actionKey;

    if (this.object.refModel) {
      actionKey = 'deleteRefModelAttachment';
    } else if (this.object.requirement) {
      actionKey = 'deleteRequirementAttachment';

      return this.removeStoryOrRequirement(actionKey);
    } else if (this.object.story) {
      actionKey = 'deleteStoryAttachment';

      return this.removeStoryOrRequirement(actionKey);
    } else if (this.object.sfNode) {
      actionKey = 'deleteOrgModelAttachment';
    } else {
      actionKey = 'deleteAttachment';
    }

    const action = {};

    action[actionKey] = {
      itemMxKey: this.object.itemMxKey,
      mxKey: Number(this.mxKey)
    };

    return this.services.diagramUpdateSvc.update(action).then(result => {
      this.redrawActivity(this.object.itemMxKey);

      return result;
    });
  }

  private getMxKey(itemMxKey, attachmentType) {
    let listKey;

    switch (attachmentType) {
      case 'refmodel':
        listKey = 'refModelAttachments';
        break;

      case 'requirement':
        listKey = 'requirementAttachments';
        break;

      case 'story':
        listKey = 'storyAttachments';
        break;

      case 'orgmodel':
        listKey = 'orgModelAttachments';
        break;

      default:
        listKey = 'attachments';
    }

    const activity = this.services.jsonHandlerSvc.get().activities.find(a => {
      return Number(a.mxKey) === Number(itemMxKey);
    });

    if (!activity[listKey]) {
      activity[listKey] = [];
    }

    // generate new mxKey
    let mxKey = 0;

    activity[listKey].forEach(function (attachment) {
      if (attachment.mxKey > mxKey) {
        mxKey = attachment.mxKey;
      }
    });

    return mxKey + 1;
  }

  private addStoryOrRequirement(action, param, type: 'story' | 'requirement') {
    return this.services.diagramUpdateSvc.update(action, true, true).then(() => {
      this.services.sanitizeXssService.sanitizeXss(param, KEYS_TO_SANITIZE.ATTACHMENT);
      const [actionType] = Object.keys(action); // here we get first object property

      action[actionType][type] = {
        id: action[actionType][type],
        status: action[actionType].status,
        issueUrl: action[actionType].issueUrl
      };

      delete action[actionType].status;

      this.services.jsonHandlerSvc.update(action);
      this.redrawActivity(this.object.itemMxKey);

      return param;
    });
  }

  private removeStoryOrRequirement(actionKey: string) {
    const action = {};

    action[actionKey] = {
      itemMxKey: this.object.itemMxKey,
      mxKey: Number(this.mxKey)
    };

    return this.services.diagramUpdateSvc.update(action, true, true).then(result => {
      this.services.jsonHandlerSvc.update(action);
      this.redrawActivity(this.object.itemMxKey);

      return result;
    });
  }

  private redrawActivity(mxKey) {
    this.services.editorRegistrySvc
      .getGraph()
      .model.getCell(Number(mxKey))
      .comp.instance.setupFromJson();
  }
}
